/**
 * Eval runs, and what changed.
 *
 * The other half of the tuning loop. Runs have been recorded since the harness
 * landed and nothing displayed them, so the only way to read a result was the
 * CLI output at the moment it ran.
 *
 * Two things are arranged deliberately:
 *
 *  - **The held-out figure is the headline.** The 90% gate is measured on that
 *    slice and nowhere else, because the tuning slice is the one we tuned
 *    against and a good score on it is a measure of our own effort. Showing
 *    "overall" first would put the flattering number where the eye lands.
 *  - **Regressions come before everything else.** A run is not interesting
 *    because it passed; it is interesting because something that used to pass
 *    now does not. That list decides whether a change ships.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FlaskConical, Play, TrendingDown, TriangleAlert } from 'lucide-react';
import { ApiError, apiGet, apiPost } from '../api/client';
import { Badge, Button, Card, CardHeader, Table, Td, Th } from '../components/ui/primitives';
import { cn } from '../lib/cn';
import { formatPercent, formatRelative } from '../lib/format';

interface RunSummary {
  id: string;
  runnerName: string;
  judgeModelId: string | null;
  partial: boolean;
  /** How many times each case ran. 1 on every run written before repeats. */
  repeats: number;
  overallPassRate: number | null;
  tuningPassRate: number | null;
  heldOutPassRate: number | null;
  gitSha: string | null;
  startedAt: string;
  finishedAt: string | null;
}

interface CaseResult {
  caseId: string;
  slug: string;
  title: string;
  intent: string;
  heldOut: boolean;
  score: number;
  passed: boolean;
  /**
   * The stored grade blob. Only the repeat summary is read here; the rest is
   * the CLI's business, and typing all of it in the browser would be a second
   * copy of a shape that changes.
   */
  detail: { repeats?: { count: number; split: boolean } } | null;
}

/**
 * How much of a pass rate is real, for one slice.
 *
 * The same arithmetic as `sliceStability` in `@sophie/eval`, deliberately
 * copied rather than imported: the web bundle cannot value-import from the
 * workspace's CommonJS packages — rollup cannot trace named exports through the
 * barrel and the build fails outright, which is how a broken bundle reached CI
 * once already. Four lines of division is the cheaper side of that trade.
 *
 * A split case is one whose repeats disagreed about pass/fail, so it could have
 * landed either way. The band is the rate with all of them pushed one way, then
 * the other. It is a floor on the uncertainty, never a bound: a case that came
 * back the same way three times can still flip on the fourth.
 */
function band(cases: CaseResult[]): {
  total: number;
  passed: number;
  pointsPerCase: number;
  split: number;
  low: number;
  high: number;
} | null {
  if (cases.length === 0) return null;
  const passed = cases.filter((c) => c.passed).length;
  const splitPassed = cases.filter((c) => c.detail?.repeats?.split === true && c.passed).length;
  const splitFailed = cases.filter((c) => c.detail?.repeats?.split === true && !c.passed).length;
  return {
    total: cases.length,
    passed,
    pointsPerCase: 100 / cases.length,
    split: splitPassed + splitFailed,
    low: (passed - splitPassed) / cases.length,
    high: (passed + splitFailed) / cases.length,
  };
}

type CaseChange =
  | 'regressed'
  | 'fixed'
  | 'added'
  | 'removed'
  | 'drifted'
  /** The verdict flipped because the pass mark moved, not because anything did. */
  | 'retuned'
  | 'unchanged';

interface CaseDiff {
  slug: string;
  change: CaseChange;
  heldOut: boolean;
  scoreDelta: number | null;
}

interface FailureMap {
  intents: Array<{ intent: string; failed: number; total: number }>;
  dimensions: Array<{
    dimension: string;
    scored: number;
    mean: number | null;
    zeros: number;
    failing: number;
    notApplicable: number;
    noJudge: number;
  }>;
  clusters: Array<{
    dimension: string;
    pattern: string;
    count: number;
    examples: string[];
    slugs: string[];
  }>;
  criticals: Array<{ slug: string; note: string }>;
}

interface RunDetail {
  /** `repeats` is absent on a response from an API older than this page. */
  run: Omit<RunSummary, 'repeats'> & { repeats?: number };
  results: CaseResult[];
  previousRunId: string | null;
  diff: { cases: CaseDiff[]; regressed: CaseDiff[]; fixed: CaseDiff[]; hasRegression: boolean };
  summary: string;
  /** Absent on a response from an API older than this page. */
  failureMap?: FailureMap;
}

export function EvalRunsPage() {
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [runOutcome, setRunOutcome] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const runs = useQuery({
    queryKey: ['eval-runs'],
    queryFn: () => apiGet<RunSummary[]>('/api/evals/runs?limit=50'),
  });

  const startRun = useMutation({
    mutationFn: () => apiPost<{ runId: string }>('/api/evals/runs', { repeats: 3 }),
    onSuccess: async () => {
      setRunOutcome('Run finished — newest row below.');
      await queryClient.invalidateQueries({ queryKey: ['eval-runs'] });
    },
    onError: async (error: unknown) => {
      // A full suite at three repeats runs for a long time and WILL outlive
      // the proxy. The run keeps going on the server and lands as a row when
      // it finishes — a client-side cutoff here is not a failure.
      setRunOutcome(
        error instanceof ApiError
          ? error.message
          : 'Still running on the server — a full suite at three repeats takes a while. Refresh this page later; the run appears as the newest row when it lands.',
      );
      await queryClient.invalidateQueries({ queryKey: ['eval-runs'] });
    },
  });

  const runButton = (
    <Button
      size="sm"
      disabled={startRun.isPending}
      onClick={() => {
        setRunOutcome(null);
        startRun.mutate();
      }}
      title="Runs every active case three times through the live agent config, majority verdict per case. Costs real model tokens and can take an hour or more."
    >
      <Play size={13} strokeWidth={1.75} aria-hidden />
      {startRun.isPending ? 'Running…' : 'Run suite ×3'}
    </Button>
  );

  if (runs.isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  if (runs.error) {
    return (
      <div className="mx-auto max-w-[700px] px-5 py-12 text-center">
        <TriangleAlert size={26} strokeWidth={1.5} className="mx-auto mb-3 text-stop-700" aria-hidden />
        <p className="text-sm text-ink-700">
          {runs.error instanceof Error ? runs.error.message : 'Could not load eval runs.'}
        </p>
      </div>
    );
  }

  if (openRunId) {
    return <RunDetailView runId={openRunId} onBack={() => setOpenRunId(null)} />;
  }

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-5">
      {(runs.data ?? []).length === 0 ? (
        <Card>
          <CardHeader title="Eval runs" action={runButton} />
          {runOutcome ? <p className="px-4 pt-3 text-xs text-ink-700">{runOutcome}</p> : null}
          <div className="px-4 py-12 text-center">
            <FlaskConical size={28} strokeWidth={1.5} className="mx-auto mb-3 text-ink-300" aria-hidden />
            <p className="text-sm text-ink-700">No eval run has been recorded.</p>
            <p className="mx-auto mt-2 max-w-[520px] text-sm text-ink-400">
              Import cases from the ops sheet, write an expectation for each, then run the suite.
              Until then the only measure of Zoye is our own opinion of it.
            </p>
          </div>
        </Card>
      ) : (
        <Card>
          <CardHeader title="Eval runs" meta="newest first" action={runButton} />
          {runOutcome ? <p className="px-4 pt-3 text-xs text-ink-700">{runOutcome}</p> : null}
          <Table>
            <thead>
              <tr>
                <Th>Started</Th>
                <Th>Runner</Th>
                <Th className="text-right">Held-out</Th>
                <Th className="text-right">Tuning</Th>
                <Th className="text-right">Overall</Th>
                <Th>Judge</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {(runs.data ?? []).map((run) => (
                <tr key={run.id} className="transition-colors duration-fast hover:bg-surface-sunken">
                  <Td>
                    <div className="text-ink-900" title={new Date(run.startedAt).toISOString()}>
                      {formatRelative(run.startedAt)}
                    </div>
                    {run.gitSha ? (
                      <div className="font-mono text-2xs text-ink-400">{run.gitSha}</div>
                    ) : null}
                  </Td>
                  <Td className="text-xs text-ink-500">
                    {run.runnerName}
                    {/* A rate from one pass and a rate from three repeats are
                        different claims, and the row must not present them as
                        the same measurement. */}
                    <div className="text-2xs text-ink-400">
                      {run.repeats > 1 ? `×${run.repeats}, majority` : 'single run'}
                    </div>
                  </Td>
                  {/* Held-out first and emphasised: it is the gated figure. */}
                  <Td className="tabular text-right font-semibold text-ink-900">
                    {formatPercent(run.heldOutPassRate, 0)}
                  </Td>
                  <Td className="tabular text-right text-ink-500">
                    {formatPercent(run.tuningPassRate, 0)}
                  </Td>
                  <Td className="tabular text-right text-ink-500">
                    {formatPercent(run.overallPassRate, 0)}
                  </Td>
                  <Td>
                    {run.partial ? (
                      <Badge tone="held">partial</Badge>
                    ) : run.judgeModelId ? (
                      <span className="text-2xs text-ink-500">{run.judgeModelId}</span>
                    ) : (
                      <span className="text-2xs text-ink-400">none</span>
                    )}
                  </Td>
                  <Td className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setOpenRunId(run.id)}>
                      Open
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="border-t border-line">
            <SliceLegend />
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * What the three percentages mean.
 *
 * Three numbers in a row with no explanation get read as "three attempts at the
 * same measurement", and the natural conclusion is that the biggest one is the
 * truth. It is the opposite: the numbers differ because they are measured on
 * DIFFERENT cases, and the smallest slice is the trustworthy one.
 */
function SliceLegend() {
  return (
    <div className="px-4 py-3 text-xs text-ink-500">
      {/* First, because it is the misreading that matters most. Every number on
          this page is measured on tickets a human said the VENDOR got wrong;
          quoting one as "Zoye handles X% of customers correctly" overstates
          the problem by leaving out every ticket that was fine all along. */}
      <p className="mb-2 rounded-md bg-surface-sunken px-2.5 py-2 text-ink-700">
        <span className="font-medium">What these are measured on.</span> Every case here is a real
        ticket a reviewer said was handled <span className="font-medium">badly</span>. So a score of
        50% means "half of the tickets that previously went wrong now go right" — not "half of all
        customers get a correct reply". Overall performance across normal tickets is not measured
        yet.
      </p>
      <p className="mb-2 text-ink-700">
        Every case is assigned to one of two slices when it is imported, by a hash of its name.
        Nobody picks — a slice somebody can choose is a slice that ends up holding the easy cases.
      </p>
      <dl className="space-y-1.5">
        <div>
          <dt className="inline font-medium text-ink-900">Held-out</dt>
          <dd className="inline">
            {' '}
            — about one case in five. We never read these failures while fixing things. Because
            nothing has been tuned to satisfy them, this is the honest estimate of how Zoye
            handles a ticket it has never been shaped around.{' '}
            <span className="text-ink-700">The 90% launch gate is measured here, and only here.</span>
          </dd>
        </div>
        <div>
          <dt className="inline font-medium text-ink-900">Tuning</dt>
          <dd className="inline">
            {' '}
            — the other four in five. These are the cases we read, diagnose and fix against. That
            makes the number useful for steering and useless as evidence: it partly measures how
            hard we worked on these specific tickets, so it will always run ahead of held-out.
          </dd>
        </div>
        <div>
          <dt className="inline font-medium text-ink-900">Overall</dt>
          <dd className="inline">
            {' '}
            — all cases together. The headline figure, and the least meaningful of the three,
            because four fifths of it is the tuning slice.
          </dd>
        </div>
      </dl>
      <p className="mt-2">
        Read them as a pair: <span className="text-ink-700">held-out is the score</span>, and the
        gap between tuning and held-out is how much of the tuning score is our own effort. A wide
        gap means we have been fitting to specific tickets rather than fixing the agent.
      </p>
    </div>
  );
}

/**
 * The failure map — what to tune, not just what failed.
 *
 * A hundred failing rows are a list; "38 of them lost points on one dimension
 * for the same worded reason" is a work item. Clusters are (dimension,
 * pattern) pairs biggest-first, which is exactly the order fixes should be
 * attempted in.
 */
function FailureMapPanel({ map }: { map: FailureMap }) {
  const failingIntents = map.intents.filter((entry) => entry.failed > 0);
  return (
    <Card className="mt-4">
      <CardHeader title="Failure map" meta="grouped by shared cause, biggest first" />

      {failingIntents.length > 0 ? (
        <div className="flex flex-wrap gap-2 px-4 pt-3">
          {failingIntents.map((entry) => (
            <span
              key={entry.intent}
              className="rounded bg-surface-sunken px-2 py-1 text-xs text-ink-700"
            >
              {entry.intent.replace(/_/g, ' ')}{' '}
              <span className="tabular font-medium text-stop-700">{entry.failed}</span>
              <span className="text-ink-400">/{entry.total}</span>
            </span>
          ))}
        </div>
      ) : null}

      {map.clusters.length > 0 ? (
        <ul className="space-y-2.5 px-4 py-3">
          {map.clusters.map((cluster) => (
            <li key={`${cluster.dimension}:${cluster.pattern}`} className="text-xs">
              <p className="text-ink-900">
                <span className="tabular font-semibold">{cluster.count}×</span>{' '}
                <span className="font-mono text-ink-500">{cluster.dimension}</span>{' '}
                {cluster.pattern}
              </p>
              {cluster.examples
                .filter((example) => example !== cluster.pattern)
                .slice(0, 2)
                .map((example) => (
                  <p key={example} className="mt-0.5 truncate text-ink-400" title={example}>
                    “{example}”
                  </p>
                ))}
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-4 py-3 text-xs text-ink-400">
          Nothing scored below 1 — every dimension came back clean.
        </p>
      )}

      {map.criticals.length > 0 ? (
        <div className="border-t border-line px-4 py-3">
          <p className="eyebrow mb-1.5 text-stop-700">
            Critical failures — these fail their case whatever the score
          </p>
          <ul className="space-y-1 text-xs">
            {map.criticals.map((critical) => (
              <li key={`${critical.slug}:${critical.note}`}>
                <span className="font-mono text-ink-500">{critical.slug}</span>{' '}
                <span className="text-ink-700">{critical.note}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="border-t border-line px-4 py-3">
        <p className="eyebrow mb-1.5 text-ink-500">What the score is made of</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-2xs uppercase tracking-wide text-ink-400">
                <th className="py-1 pr-3 font-medium">Dimension</th>
                <th className="py-1 pr-3 text-right font-medium">Scored</th>
                <th className="py-1 pr-3 text-right font-medium">Mean</th>
                <th className="py-1 pr-3 text-right font-medium">Zeros</th>
                <th className="py-1 pr-3 text-right font-medium">Failing</th>
                <th className="py-1 pr-3 text-right font-medium">N/A</th>
              </tr>
            </thead>
            <tbody>
              {map.dimensions.map((dimension) => (
                <tr key={dimension.dimension} className="text-ink-700">
                  <td className="py-0.5 pr-3 font-mono">{dimension.dimension}</td>
                  <td className="tabular py-0.5 pr-3 text-right">{dimension.scored}</td>
                  <td className="tabular py-0.5 pr-3 text-right">
                    {dimension.mean === null ? '—' : dimension.mean.toFixed(2)}
                  </td>
                  <td className="tabular py-0.5 pr-3 text-right">{dimension.zeros}</td>
                  <td className="tabular py-0.5 pr-3 text-right">{dimension.failing}</td>
                  <td className="tabular py-0.5 pr-3 text-right">{dimension.notApplicable}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}

const CHANGE_TONE: Record<CaseChange, 'fact' | 'stop' | 'held' | 'neutral'> = {
  fixed: 'fact',
  regressed: 'stop',
  drifted: 'held',
  added: 'neutral',
  removed: 'neutral',
  // Neutral, deliberately. The verdict flipped because somebody moved the pass
  // mark; nothing about the agent changed, and colouring it red would put a
  // decision we made in the column that decides whether to ship.
  retuned: 'neutral',
  unchanged: 'neutral',
};

function RunDetailView({ runId, onBack }: { runId: string; onBack: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['eval-run', runId],
    queryFn: () => apiGet<RunDetail>(`/api/evals/runs/${runId}`),
  });

  if (isLoading) return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  if (error || !data) {
    return (
      <div className="mx-auto max-w-[700px] px-5 py-12 text-center">
        <p className="text-sm text-ink-700">
          {error instanceof Error ? error.message : 'That run could not be loaded.'}
        </p>
      </div>
    );
  }

  const changed = data.diff.cases.filter((c) => c.change !== 'unchanged');
  const byChange = new Map(data.diff.cases.map((c) => [c.slug, c]));
  const repeats = data.run.repeats ?? 1;
  const heldOutBand = band(data.results.filter((r) => r.heldOut));
  const overallBand = band(data.results);

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-5">
      <button
        type="button"
        onClick={onBack}
        className="mb-3 inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900"
      >
        <ArrowLeft size={14} strokeWidth={1.75} aria-hidden />
        All runs
      </button>

      {data.diff.hasRegression ? (
        // First on the page, before any pass rate. A rate can improve while
        // something that used to work stops working, and the rate is the part
        // people quote.
        <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-line bg-stop-100 px-4 py-3">
          <TrendingDown size={16} strokeWidth={1.75} className="mt-px shrink-0 text-stop-700" aria-hidden />
          <div className="text-xs text-stop-700">
            <span className="font-semibold">
              {data.diff.regressed.length} case(s) passed before and fail now.
            </span>{' '}
            {data.diff.regressed.map((c) => c.slug).join(', ')}. A rising pass rate does not make
            this acceptable — something that worked has stopped working.
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
        <Figure
          label="Held-out"
          value={formatPercent(data.run.heldOutPassRate, 0)}
          note={
            heldOutBand
              ? `the gated slice · 1 case = ${heldOutBand.pointsPerCase.toFixed(1)}pts`
              : 'the gated slice'
          }
          emphasis
        />
        <Figure label="Tuning" value={formatPercent(data.run.tuningPassRate, 0)} note="tuned against" />
        <Figure
          label="Overall"
          value={formatPercent(data.run.overallPassRate, 0)}
          note={`${data.results.length} cases`}
        />
        <Figure
          label="Changed"
          value={String(changed.length)}
          note={data.previousRunId ? 'vs the previous run' : 'first run — nothing to compare'}
        />
      </div>

      <Stability
        repeats={repeats}
        heldOut={heldOutBand}
        overall={overallBand}
        changed={changed.length}
      />

      {/* The same explanation as the list, because this is where somebody is
          actually staring at the three numbers and deciding what they mean. */}
      <div className="mt-3 rounded-lg border border-line">
        <SliceLegend />
      </div>

      {data.run.partial ? (
        <p className="mt-3 text-xs text-hold-700">
          This run was partial: judged dimensions were skipped for want of a judge model, so
          grounding and tone were not scored.
        </p>
      ) : null}

      {data.failureMap ? <FailureMapPanel map={data.failureMap} /> : null}

      <Card className="mt-4">
        <CardHeader title="Cases" meta="failures first" />
        <Table>
          <thead>
            <tr>
              <Th>Case</Th>
              <Th>Intent</Th>
              <Th>Slice</Th>
              <Th className="text-right">Score</Th>
              <Th>Result</Th>
              <Th>Change</Th>
            </tr>
          </thead>
          <tbody>
            {data.results.map((result) => {
              const change = byChange.get(result.slug);
              return (
                <tr key={result.caseId}>
                  <Td>
                    <div className="font-medium text-ink-900">{result.title}</div>
                    <div className="font-mono text-2xs text-ink-400">{result.slug}</div>
                  </Td>
                  <Td className="text-xs text-ink-500">{result.intent.replace(/_/g, ' ')}</Td>
                  <Td>
                    {result.heldOut ? (
                      <Badge tone="accent">held-out</Badge>
                    ) : (
                      <span className="text-2xs text-ink-400">tuning</span>
                    )}
                  </Td>
                  <Td className="tabular text-right text-ink-700">{result.score.toFixed(2)}</Td>
                  <Td>
                    <Badge tone={result.passed ? 'fact' : 'stop'}>
                      {result.passed ? 'pass' : 'FAIL'}
                    </Badge>
                  </Td>
                  <Td>
                    {change && change.change !== 'unchanged' ? (
                      <span className="flex items-center gap-1.5">
                        <Badge tone={CHANGE_TONE[change.change]}>{change.change}</Badge>
                        {change.scoreDelta !== null && change.scoreDelta !== 0 ? (
                          <span
                            className={cn(
                              'tabular text-2xs',
                              change.scoreDelta > 0 ? 'text-ok-700' : 'text-stop-700',
                            )}
                          >
                            {change.scoreDelta > 0 ? '+' : ''}
                            {change.scoreDelta.toFixed(2)}
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-2xs text-ink-300">—</span>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <p className="border-t border-line px-4 py-2.5 text-xs text-ink-400">{data.summary}</p>
      </Card>
    </div>
  );
}

/**
 * How much of the numbers above is real.
 *
 * Sits directly under the figures because that is where the misreading happens.
 * Held-out is thirteen cases, so one of them is 7.7 points — a 54% quoted
 * against a 90% gate could honestly have been 38% or 69% with nothing changed,
 * and the same commit has moved ten of fifty-five cases run to run.
 *
 * The two states are worded apart on purpose. A run that repeated nothing has
 * not measured its own stability and has to say so; showing a zero-width range
 * would read as "we checked and it was perfectly stable", which is the opposite
 * claim.
 */
function Stability({
  repeats,
  heldOut,
  overall,
  changed,
}: {
  repeats: number;
  heldOut: ReturnType<typeof band>;
  overall: ReturnType<typeof band>;
  changed: number;
}) {
  const measured = repeats > 1;
  const range = (b: NonNullable<ReturnType<typeof band>>): string =>
    `${Math.round(b.low * 100)}–${Math.round(b.high * 100)}%`;

  return (
    <div className="mt-3 rounded-lg border border-line bg-surface px-4 py-3 text-xs">
      <div className="eyebrow mb-1.5">How much of that is real</div>
      {measured ? (
        <p className="text-ink-700">
          Each case ran <span className="font-medium">{repeats} times</span> and the verdict above
          is the majority of those, with a tie counted as a failure.{' '}
          {overall && overall.split > 0 ? (
            <>
              <span className="font-medium">
                {overall.split} of {overall.total} case(s) did not agree with themselves
              </span>{' '}
              — held-out plausibly sits anywhere in {heldOut ? range(heldOut) : '—'}, and overall in{' '}
              {range(overall)}. A change worth fewer cases than that cannot be told apart from the
              noise{changed > 0 ? `, which includes some of the ${changed} changed here` : ''}.
            </>
          ) : (
            <>Every case agreed with itself. That is a floor on the uncertainty, not a bound — a
            case that came back the same way {repeats} times can still flip on the next one.</>
          )}
        </p>
      ) : (
        <p className="text-ink-700">
          <span className="font-medium">Not measured.</span> Each case ran once, so this run cannot
          bound its own error. The same commit has moved about ten of fifty-five cases with no code
          change at all, which means a difference of a few cases against the previous run
          {changed > 0 ? ` — including the ${changed} shown here — ` : ' '}
          is not evidence either way. Re-run with{' '}
          <code className="text-2xs">--repeats=3</code> for a majority verdict and a measured range.
        </p>
      )}
    </div>
  );
}

function Figure({
  label,
  value,
  note,
  emphasis = false,
}: {
  label: string;
  value: string;
  note: string;
  emphasis?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-lg bg-surface px-4 py-3 shadow-hairline',
        emphasis ? 'outline outline-1 outline-accent-300' : '',
      )}
    >
      <div className="eyebrow">{label}</div>
      <div className="tabular mt-1.5 text-2xl font-semibold text-ink-900">{value}</div>
      <div className="mt-0.5 text-xs text-ink-400">{note}</div>
    </div>
  );
}
